// src/content/relics.ts
//
// Phase 2 catalogue: 8 relic templates as RelicDef (Omit<Relic, 'id'>).
// Relic interface has no `effect` field — relic logic is wired in Phase 8 via
// the event-bus hooks (onSpinStart / onScore / onRoundClear / onZero).
//
// All values sourced verbatim from
// .planning/phases/02-data-model-content-catalogues/02-CONTEXT.md.

import type { RelicDef } from '../state/types';

export const RELICS: readonly RelicDef[] = Object.freeze([
  {
    defId: 'croupiers_smile',
    name: "Croupier's Smile",
    description: 'Place 4 fiches per spin instead of 3.',
    rarity: 'rare',
  },
  {
    defId: 'lucky_charm',
    name: 'Lucky Charm',
    description: '+5 mult to all fiches on the first spin of every round.',
    rarity: 'common',
  },
  {
    defId: 'house_edge',
    name: 'House Edge',
    description: 'All payouts +50%, but lose $1 per spin.',
    rarity: 'uncommon',
  },
  {
    defId: 'cold_streak',
    name: 'Cold Streak',
    description: 'Each consecutive black hit increases mult by +1 (resets on red or 0).',
    rarity: 'uncommon',
  },
  {
    defId: 'hot_streak',
    name: 'Hot Streak',
    description: 'Each consecutive red hit increases mult by +1 (resets on black or 0).',
    rarity: 'uncommon',
  },
  {
    defId: 'safety_net',
    name: 'Safety Net',
    description: 'The first 0 in any round is treated as a re-spin (free).',
    rarity: 'rare',
  },
  // 12b (RELIC-07): second_ball — passive rare relic. Its effect is the
  // queuedBall slot grant, read at the shop UI layer in plan 12-05's
  // replace-ball flow (relics.some(r => r.defId === 'second_ball')). No
  // RELIC_HANDLERS entry — mirror croupiers_smile / peek (passive relics
  // with no event-bus hook).
  {
    defId: 'second_ball',
    name: 'Second Ball',
    description:
      'Reserves an extra ball slot. Buying a different ball when held queues it instead of replacing your current ball.',
    rarity: 'rare',
  },
  {
    defId: 'compound_interest',
    name: 'Compound Interest',
    description: 'Interest cap raised from $5 to $10.',
    rarity: 'common',
  },
  {
    defId: 'peek',
    name: 'Peek',
    description: 'Preview your next fiche draw. What you see is what you get.',
    rarity: 'common',
  },
  // Phase 13 (RELIC-09): void-synergy item per CONTEXT.md "Void Synergy Item".
  // +1 mult per void slot on the wheel, applied to every fiche on every spin
  // via SpinContext.flatMultBonus (same chassis as lucky_charm). Becomes
  // valuable once Phase 15 consumables introduce deck-mutators that add
  // void positions to the wheel.
  {
    defId: 'void_walker',
    name: 'Void Walker',
    description: '+1 mult per void slot on the wheel, every spin.',
    rarity: 'rare',
  },
  {
    defId: 'reliable',
    name: 'Reliable',
    description: 'Your second-highest scoring fiche this round always returns to your hand.',
    rarity: 'rare',
  },
  {
    defId: 'golden_touch',
    name: 'Golden Touch',
    description: 'Your highest scoring fiche this round always returns to your hand.',
    rarity: 'legendary',
  },
  {
    defId: 'one_for_all',
    name: 'One for All',
    description: 'Covering any subcell of a dozen covers the entire dozen (all 12 numbers).',
    rarity: 'rare',
  },
] as const);

export const RELICS_BY_ID: Readonly<Record<string, RelicDef>> = Object.freeze(
  RELICS.reduce<Record<string, RelicDef>>((acc, def) => {
    acc[def.defId] = def;
    return acc;
  }, {}),
);

const RELIC_SHEET_1: Readonly<Record<string, number>> = {
  croupiers_smile: 0,
  lucky_charm: 1,
  house_edge: 2,
  cold_streak: 3,
  hot_streak: 4,
  safety_net: 5,
};

const RELIC_SHEET_2: Readonly<Record<string, number>> = {
  second_ball: 0,
  compound_interest: 1,
  peek: 2,
  void_walker: 3,
  reliable: 4,
  golden_touch: 5,
};

const RELIC_SHEET_3: Readonly<Record<string, number>> = {
  one_for_all: 0,
};

export function relicSpritesheet(defId: string): { key: string; frame: number } | undefined {
  const r1 = RELIC_SHEET_1[defId];
  if (r1 !== undefined) return { key: 'relics-1', frame: r1 };
  const r2 = RELIC_SHEET_2[defId];
  if (r2 !== undefined) return { key: 'relics-2', frame: r2 };
  const r3 = RELIC_SHEET_3[defId];
  if (r3 !== undefined) return { key: 'relics-3', frame: r3 };
  return undefined;
}
